import { lazy, Suspense, useEffect } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Notice } from './components/Notice';
import { platformBridge } from './services/platform-bridge';
import { useAppStore } from './stores/app-store';

const EditorScreen = lazy(() => import('./screens/EditorScreen').then((module) => ({ default: module.EditorScreen })));
const HomeScreen = lazy(() => import('./screens/HomeScreen').then((module) => ({ default: module.HomeScreen })));

export default function App() {
  const route = useAppStore((state) => state.route);
  const setRoute = useAppStore((state) => state.setRoute);
  const openProject = useAppStore((state) => state.openProject);
  const openIncomingFiles = useAppStore((state) => state.openIncomingFiles);

  useEffect(() => {
    const syncRoute = () => {
      const [, section = '', id] = window.location.hash.split('/');
      if (section === 'editor' && id) void openProject(id);
      else setRoute('home');
    };
    syncRoute();
    window.addEventListener('hashchange', syncRoute);
    return () => window.removeEventListener('hashchange', syncRoute);
  }, [openProject, setRoute]);

  useEffect(() => {
    let disposed = false;
    let unlisten: (() => void) | undefined;
    const receiveFiles = () => void platformBridge.receiveExternalFiles().then((files) => {
      if (!disposed && files.length) void openIncomingFiles(files);
    });
    receiveFiles();
    void import('@tauri-apps/api/event')
      .then(({ listen }) => listen('desktop-files-ready', receiveFiles))
      .then((stop) => { if (disposed) stop(); else unlisten = stop; })
      .catch(() => undefined);
    return () => { disposed = true; unlisten?.(); };
  }, [openIncomingFiles]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<main className="app-loading"><span className="spinner" /><p>Opening RealImager…</p></main>}>
        {route === 'editor' ? <EditorScreen /> : <HomeScreen />}
      </Suspense>
      <Notice />
    </ErrorBoundary>
  );
}
